import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { AlertTriangle, CircleAlert, ExternalLink, Layers3 } from "lucide-react";
import type { MouseEvent } from "react";
import { Alert, AlertAction, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import type { DataCatalogEntryNodeOptions } from "../extensions/data-catalog-entry-node";
import { CatalogProviderIcon, getCatalogProviderDisplayName } from "./catalog-provider-branding";
import {
  catalogStatusBadgeLabel,
  type DataCatalogEntryAttrs,
  normalizeDataCatalogEntryAttrs,
  resolveFieldGroupRenderedLabel,
  resolveRenderedLabel,
} from "./data-catalog-entry-shared";

function openDetails(
  options: DataCatalogEntryNodeOptions | undefined,
  attrs: DataCatalogEntryAttrs,
  event: MouseEvent
) {
  event.preventDefault();
  event.stopPropagation();
  options?.onOpenDetails?.(attrs);
}

function formatStaleLabel(lastSyncedAt: number | null) {
  if (!lastSyncedAt) return "Catalog data may be outdated. Last synced time is unavailable.";
  const deltaMs = Date.now() - lastSyncedAt;
  const days = Math.floor(deltaMs / 86_400_000);
  if (days < 1) return "Catalog data may be outdated. Last synced today.";
  return `Catalog data may be outdated. Last synced ${days} day${days !== 1 ? "s" : ""} ago.`;
}

export function DataCatalogEntryNodeView({ node, extension, selected }: NodeViewProps) {
  const attrs = normalizeDataCatalogEntryAttrs(node.attrs as Record<string, unknown>);
  const label = resolveRenderedLabel(node.attrs as Record<string, unknown>);
  const fieldGroupSummary = resolveFieldGroupRenderedLabel(
    attrs.selectedFieldLabels,
    attrs.selectedFieldValues
  );
  const options = extension.options as DataCatalogEntryNodeOptions | undefined;
  const isFieldGroup = attrs.embedKind === "fieldGroup";
  const isBroken = attrs.status === "broken";
  const isStale = attrs.status === "stale";
  const isUnverifiable = attrs.status === "unverifiable";
  const statusBadgeLabel = catalogStatusBadgeLabel(attrs.status);
  const handleOpenExternal = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void options?.onOpenExternal?.(attrs);
  };
  const handleRefresh = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    options?.onRefresh?.(attrs);
  };

  return (
    <NodeViewWrapper as="span" contentEditable={false} className="inline-flex align-baseline">
      <HoverCard>
        <HoverCardTrigger
          render={
            <button
              type="button"
              className={cn(
                "df-data-catalog-entry",
                selected && "ring-2 ring-primary/40",
                isBroken && "df-data-catalog-entry--broken",
                isStale && "df-data-catalog-entry--stale",
                isUnverifiable && "df-data-catalog-entry--unverifiable"
              )}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={(event) => openDetails(options, attrs, event)}
            >
              <CatalogProviderIcon providerKey={attrs.providerKey} className="size-3 shrink-0" />
              <span>{label}</span>
              {attrs.status !== "current" ? (
                <span className="df-data-catalog-entry__status">{statusBadgeLabel}</span>
              ) : null}
            </button>
          }
        />
        <HoverCardContent className="w-80 space-y-3 p-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">{attrs.objectDisplayLabel || label}</p>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <CatalogProviderIcon providerKey={attrs.providerKey} className="size-3.5 shrink-0" />
              <span>
                {attrs.catalogDisplayName}
                {attrs.providerKey ? ` · ${getCatalogProviderDisplayName(attrs.providerKey)}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Layers3 className="size-3.5" />
              <span>{attrs.objectCategory || "Catalog object"}</span>
            </div>
          </div>
          {isBroken ? (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertTitle>
                {attrs.brokenReason || "This linked catalog entry is broken and needs attention."}
              </AlertTitle>
            </Alert>
          ) : null}
          {isStale ? (
            <Alert variant="warning">
              <AlertTriangle />
              <AlertTitle>{formatStaleLabel(attrs.lastSyncedAt)}</AlertTitle>
              {options?.onRefresh ? (
                <AlertAction>
                  <Button type="button" variant="outline" size="sm" onClick={handleRefresh}>
                    Refresh
                  </Button>
                </AlertAction>
              ) : null}
            </Alert>
          ) : null}
          {isUnverifiable ? (
            <Alert>
              <AlertTriangle />
              <AlertTitle>
                Catalog data could not be verified. Existing content is preserved and may be
                outdated.
              </AlertTitle>
              {options?.onRefresh ? (
                <AlertAction>
                  <Button type="button" variant="outline" size="sm" onClick={handleRefresh}>
                    Retry
                  </Button>
                </AlertAction>
              ) : null}
            </Alert>
          ) : null}
          {isFieldGroup ? (
            <div className="space-y-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Linked catalog values
              </p>
              <p className="text-xs leading-5">{fieldGroupSummary || "Linked catalog values"}</p>
              {attrs.selectedFieldLabels.length > 0 && attrs.selectedFieldValues.length > 0 ? (
                <p className="text-[11px] leading-5 text-muted-foreground">
                  Fields: {attrs.selectedFieldLabels.join(", ")}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Click to inspect provider-native details for this linked catalog object.
            </p>
          )}
          {options?.onOpenExternal ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 w-full justify-start gap-1.5 text-xs"
              onClick={handleOpenExternal}
            >
              <ExternalLink className="size-3.5" />
              Open in catalog
            </Button>
          ) : null}
        </HoverCardContent>
      </HoverCard>
    </NodeViewWrapper>
  );
}
